import { useBreakpointValue } from '@wegroup/design-system';
import React from 'react';
import { Enterprise } from '../../../../../../../types/ProductFactory';
import ErrorPage from '../../../../../../common/src/components/ErrorPage';
import CbeConfirmation from './CbeConfirmation';
import CbeData from './CbeData';
import CbeLanguage from './CbeLanguage';
import CbeSearch from './CbeSearch';
import { string, object } from 'yup';
import { useTranslation } from 'react-i18next';
import cogoToast from 'cogo-toast';
import { isEmpty } from 'lodash';
import {
  FlowsQuestion,
  FlowsQuestionWithNewEnum,
} from '../../../../types/FlowsTypes';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import useSubView from '../../../../hooks/useSubView';
import useFlowQuestions from '../../../../hooks/useFlowQuestions';
import { useFlowsProgression } from '../../../../hooks/useFlowsProgression';
import defaultLanguageQuestion from '../../../ui/advisory-flow/constants/DefaultLanguageQuestion.json';

interface Props {
  question: FlowsQuestion;
}

const CbeInput: React.FC<React.PropsWithChildren<Props>> = ({ question }) => {
  const { t } = useTranslation();
  const questions = useFlowQuestions();

  const {
    field: { value, onBlur },
  } = useFlowInputComponent({
    question,
  });

  const { nextQuestion, previousQuestion } = useFlowsProgression();

  const { currentSubView, nextSubView, previousSubView } = useSubView({
    question,
    defaultView: 'cbeSearch',
  });

  const isMobile = useBreakpointValue({
    base: true,
    laptop: false,
  });

  const legalLanguageQuestion =
    questions.find(({ id }) => id === 'company.language') ||
    questions.find(({ id }) => id === 'policy_holder.language') ||
    (defaultLanguageQuestion as FlowsQuestionWithNewEnum);

  const company = (value as { _local: Enterprise })?._local;
  const language = (value as { 'company.language': string })?.[
    'company.language'
  ];

  const handleOnChange = (arg0: Enterprise | boolean) =>
    onBlur({ name: question.id, value: { ...value, _local: arg0 } });

  const handleLanguageChange = (language: string) => {
    if (language) {
      onBlur({
        name: question.id,
        value: {
          ...(value as Record<string, unknown>),
          'company.language': language,
        },
      });
    }
  };

  const onContinue = (values: Enterprise) => {
    onBlur({
      name: question.id,
      value: {
        _local: values,
        'company.name': values.name,
        'company.cbe': values.cbe,
        'company.address': values.address,
      },
    });

    if (legalLanguageQuestion) nextSubView('cbeLanguage');
    else nextQuestion();
  };

  const handleNextView = () => {
    // checks if address is valid
    const addressSchema = object().shape({
      country_code: string(),
      street: string().required(),
      housenr: string().required(),
      boxnr: string().nullable(),
      zipcode: string().required(),
      city: string().required(),
    });
    try {
      addressSchema.validate(company.address);
      nextSubView('cbeConfirmation');
    } catch {
      cogoToast.info(
        t(
          'Oops there is some data missing. Please fill in the empty required fields',
        ),
        { hideAfter: 7 },
      );
      nextSubView('cbeData');
    }
  };

  const handleOnCantFindCompany = () => {
    onBlur({ name: 'company.company_search', value: { _local: {} } });
    nextSubView('cbeData');
  };

  return (
    <>
      {{
        cbeSearch: (
          <CbeSearch
            previousQuestionHandler={previousQuestion}
            nextSubView={handleNextView}
            onCantFind={handleOnCantFindCompany}
            handleOnChange={handleOnChange}
            hasValue={!isEmpty(company)}
          />
        ),
        cbeConfirmation: (
          <CbeConfirmation
            isMobile={isMobile}
            company={company}
            previousSubView={previousSubView}
            nextQuestionHandler={() => onContinue(company)}
            nextSubView={nextSubView}
          />
        ),
        cbeData: (
          <CbeData
            isMobile={isMobile}
            company={company}
            previousSubView={previousSubView}
            nextQuestionHandler={onContinue}
            handleOnChange={handleOnChange}
          />
        ),
        cbeLanguage: (
          <CbeLanguage
            isMobile={isMobile}
            language={language}
            previousSubView={previousSubView}
            nextQuestionHandler={nextQuestion}
            handleChange={handleLanguageChange}
            legalLanguageQuestion={legalLanguageQuestion}
          />
        ),
      }[currentSubView] || <ErrorPage />}
    </>
  );
};

export default CbeInput;
